import {
  Col,
  Form,
  Input,
  Radio,
  Row,
  List,
  message,
  Table,
  Button,
  Space,
  Tag,
  Checkbox,
  Divider,
  Typography,
  Tooltip
} from "antd";
import { SearchOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { convertArrayToObject } from "helper/util";
import { sortBy } from "lodash";
import sumBy from "lodash/sumBy";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef
} from "react";
import warehouseApi from "modules/client/warehouses/api/warehouseApi";
import "./PackForm.scss";

const { Text } = Typography;

// const typesDefault = {
//   micro: {
//     maxLength: 32,
//     maxWeight: 70,
//     maxVolume: 8
//   },
//   small: {
//     maxLength: 40,
//     maxWeight: 74,
//     maxVolume: 10
//   },
//   medium: {
//     maxLength: 84,
//     maxWeight: 102,
//     maxVolume: 20
//   },
//   large: {
//     maxLength: 100,
//     maxWeight: 114,
//     maxVolume: 30
//   }
// };

const initValue = {
  packageTypeId: null,
  quantity: null,
  name: null,
  maxHeight: null,
  maxLength: null,
  maxWeight: null,
  maxVolume: null,
  barcode: null
};

const PackForm = forwardRef(
  (
    {
      packageTypeList,
      pack,
      formPack,
      onOk,
      poolPackages = [],
      pickupLocationId,
      currentPickupId,
      inventoryPackagesByPickup = {},
      pickups = [],
      onCancel,
      warehouseOnly = false,
      customOnly = false,
      visible = false
    },
    ref
  ) => {
    const objPackageTypeList = convertArrayToObject(packageTypeList, "id");
    const [packageType, setPackageType] = useState(null);
    const [quantityCurrent, setQuantityCurrent] = useState(0);
    const [packageSourceSelection, setPackageSourceSelection] = useState("custom");
    const [warehouses, setWarehouses] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [warehouseSearchText, setWarehouseSearchText] = useState("");
    const [itemSearchTexts, setItemSearchTexts] = useState({});
    const [serialSearchTexts, setSerialSearchTexts] = useState({});
    const [selectedItems, setSelectedItems] = useState({});
    const [expandedWarehouses, setExpandedWarehouses] = useState([]);
    const [expandedItems, setExpandedItems] = useState([]);
    const [warehouseSortOrder, setWarehouseSortOrder] = useState("descending");
    const prevVisibleRef = useRef(false);

    useImperativeHandle(ref, () => ({
      handleWarehouseCancel,
      handleWarehouseConfirm,
      packageSourceSelection
    }));

    const loadWarehousesAndInventory = useCallback(async () => {
      if (!pickupLocationId) {
        setWarehouses([]);
        setInventoryItems([]);
        return;
      }
      setLoading(true);
      try {
        const warehousesData = await warehouseApi.getWarehousesByLocation(
          pickupLocationId
        );
        setWarehouses(warehousesData);
        const allInventory = await warehouseApi.getInventoryItems();
        setInventoryItems(allInventory);
      } catch (error) {
        console.error("Error loading warehouse data:", error);
        message.error("Failed to load warehouse inventory for this location");
        setWarehouses([]);
        setInventoryItems([]);
      } finally {
        setLoading(false);
      }
    }, [pickupLocationId]);

    useEffect(() => {
      if (pack) {
        setPackageType(objPackageTypeList[pack.packageTypeId]);
        formPack.setFieldsValue({ ...pack, barcode: pack.barcode });
        if (pack.dropoffs?.length) {
          setQuantityCurrent(sumBy(pack.dropoffs, "quantity"));
        } else {
          setQuantityCurrent(0);
        }
      } else {
        setPackageType(null);
        formPack.setFieldsValue(initValue);
        setQuantityCurrent(0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pack, formPack]);

    useEffect(() => {
      if (pickupLocationId) {
        loadWarehousesAndInventory();
      } else {
        setWarehouses([]);
        setInventoryItems([]);
      }
    }, [pickupLocationId, loadWarehousesAndInventory]);

    useEffect(() => {
      if (warehouseOnly) {
        setPackageSourceSelection("warehouse");
      } else if (pack?.inventoryItemId) {
        setPackageSourceSelection("warehouse");
      } else {
        setPackageSourceSelection("custom");
      }
    }, [pack, warehouseOnly]);

    // Separate effect to restore selected items when editing warehouse packages
    // Triggers when modal opens (visible changes from false to true)
    useEffect(() => {
      const isModalOpening = visible && !prevVisibleRef.current;
      prevVisibleRef.current = visible;

      // Only restore on modal opening or when inventory items load while modal is open
      if (
        visible &&
        pack?.inventoryItemId &&
        pack.inventoryItemSerials &&
        inventoryItems.length > 0
      ) {
        const item = inventoryItems.find((i) => i.id === pack.inventoryItemId);
        if (item) {
          // Restore the selection from pack.inventoryItemSerials
          setSelectedItems({
            [item.id]: {
              item,
              serials: pack.inventoryItemSerials || []
            }
          });

          if (item.warehouseId) {
            setExpandedWarehouses([item.warehouseId]);
            setExpandedItems([item.id]);
          }
        }
      }

      // Clear selections when not editing a warehouse package
      if (visible && !pack?.inventoryItemId) {
        setSelectedItems({});
        setExpandedWarehouses([]);
        setExpandedItems([]);
      }
    }, [pack, inventoryItems, visible]);

    const onChangeType = (e) => {
      setPackageType(objPackageTypeList[e.target.value]);
    };

    const handlePackageSourceTabClick = (selectedPackageSource) => {
      const hasWarehouseItems = Object.keys(selectedItems).length > 0;
      const hasCustomData =
        formPack.getFieldValue("quantity") || formPack.getFieldValue("packageTypeId");

      if (selectedPackageSource === "warehouse" && hasCustomData) {
        return;
      }
      if (selectedPackageSource === "custom" && hasWarehouseItems) {
        return;
      }

      setPackageSourceSelection(selectedPackageSource);
    };

    const groupedInventory = useMemo(() => {
      const grouped = {};
      inventoryItems.forEach((item) => {
        if (!grouped[item.warehouseId]) {
          grouped[item.warehouseId] = [];
        }
        grouped[item.warehouseId].push(item);
      });
      return grouped;
    }, [inventoryItems]);

    const filteredInventory = useMemo(() => {
      if (!warehouseSearchText) return groupedInventory;
      const searchTermLowerCase = warehouseSearchText.toLowerCase();
      const filtered = {};

      Object.keys(groupedInventory).forEach((warehouseId) => {
        const warehouseObject = warehouses.find((w) => w.id === warehouseId);
        const warehouseNameMatches = warehouseObject?.name
          ?.toLowerCase()
          .includes(searchTermLowerCase);

        if (warehouseNameMatches) {
          filtered[warehouseId] = groupedInventory[warehouseId];
        }
      });

      return filtered;
    }, [groupedInventory, warehouseSearchText, warehouses]);

    const sortedWarehouseIds = useMemo(() => {
      const warehouseIds = Object.keys(filteredInventory);
      const warehouseWithCounts = warehouseIds.map((warehouseId) => ({
        warehouseId,
        itemCount: filteredInventory[warehouseId].length
      }));

      warehouseWithCounts.sort((warehouseA, warehouseB) => {
        if (warehouseSortOrder === "ascending") {
          return warehouseA.itemCount - warehouseB.itemCount;
        } else {
          return warehouseB.itemCount - warehouseA.itemCount;
        }
      });

      return warehouseWithCounts.map((warehouse) => warehouse.warehouseId);
    }, [filteredInventory, warehouseSortOrder]);

    const toggleWarehouseSortOrder = () => {
      setWarehouseSortOrder(
        warehouseSortOrder === "ascending" ? "descending" : "ascending"
      );
    };

    const handleSerialSelection = (itemId, serial, checked) => {
      setSelectedItems((prevSelectedItems) => {
        const updatedSelectedItems = { ...prevSelectedItems };

        if (!updatedSelectedItems[itemId]) {
          const inventoryItem = inventoryItems.find((item) => item.id === itemId);
          updatedSelectedItems[itemId] = {
            item: inventoryItem,
            serials: []
          };
        }

        if (checked) {
          serial.itemName = updatedSelectedItems[itemId].item.name;
          serial.itemId = updatedSelectedItems[itemId].item.itemId;
          updatedSelectedItems[itemId].serials.push(serial);
        } else {
          updatedSelectedItems[itemId].serials = updatedSelectedItems[
            itemId
          ].serials.filter(
            (existingSerial) => existingSerial.serialNumber !== serial.serialNumber
          );
          if (updatedSelectedItems[itemId].serials.length === 0) {
            delete updatedSelectedItems[itemId];
          }
        }

        return updatedSelectedItems;
      });
    };

    const handleItemSelection = (item, checked) => {
      setSelectedItems((prev) => {
        const newSelected = { ...prev };
        if (checked) {
          const serials = (item.serialNumbersList || []).map((serial) => {
            serial.itemName = item.name;
            serial.itemId = item.itemId;
            return serial;
          });
          newSelected[item.id] = {
            item,
            serials
          };
        } else {
          delete newSelected[item.id];
        }
        return newSelected;
      });
    };

    const isItemSelected = (itemId) => {
      return !!selectedItems[itemId];
    };

    const isSerialSelected = (itemId, serial) => {
      return (
        selectedItems[itemId]?.serials?.some(
          (s) => s.serialNumber === serial.serialNumber
        ) || false
      );
    };

    const getAllocatedSerials = useCallback(() => {
      const allocated = {};
      Object.entries(inventoryPackagesByPickup).forEach(([pickupId, packages]) => {
        if (pickupId !== currentPickupId) {
          Object.values(packages).forEach((pkg) => {
            pkg.serials?.forEach((serial) => {
              if (!allocated[serial.serialNumber]) {
                allocated[serial.serialNumber] = pickupId;
              }
            });
          });
        }
      });
      return allocated;
    }, [inventoryPackagesByPickup, currentPickupId]);

    const allocatedSerials = useMemo(() => getAllocatedSerials(), [getAllocatedSerials]);

    const handleWarehouseConfirm = useCallback(() => {
      console.log("handleWarehouseConfirm called, selectedItems:", selectedItems);

      const hasCustomFormData =
        formPack.getFieldValue("quantity") || formPack.getFieldValue("packageTypeId");
      if (hasCustomFormData && !pack?.inventoryItemId) {
        message.error(
          "Cannot mix custom package with warehouse items. Please clear custom fields or switch to Custom tab."
        );
        return;
      }

      const summary = Object.values(selectedItems).map((selection) => ({
        item: selection.item,
        serials: selection.serials
      }));
      console.log("summary:", summary);
      if (summary.length === 0) {
        message.warning("Please select at least one item");
        return;
      }

      const totalSerials = summary.reduce((sum, item) => sum + item.serials.length, 0);

      const firstSelectedItem = summary[0].item;
      const selectedItemPackageType = packageTypeList.find(
        (packageTypeOption) => packageTypeOption.id === firstSelectedItem.packageTypeId
      );

      const inventoryPackage = {
        id: pack?.id || "",
        name: selectedItemPackageType?.name || firstSelectedItem.name,
        type: selectedItemPackageType?.name,
        packageTypeId: firstSelectedItem.packageTypeId,
        quantity: totalSerials,
        inventoryItemId: firstSelectedItem.id,
        inventoryItemSerials: summary.flatMap((selection) =>
          selection.serials.map((serial) => ({
            ...serial,
            itemName: selection.item.name,
            itemId: selection.item.itemId
          }))
        ),
        maxLength: selectedItemPackageType?.maxLength || 0,
        maxWeight: selectedItemPackageType?.maxWeight || 0,
        maxVolume: selectedItemPackageType?.maxVolume || 0
      };

      console.log("Inventory package being saved:", inventoryPackage);
      onOk && onOk(inventoryPackage);

      setSelectedItems({});
      setWarehouseSearchText("");
      setItemSearchTexts({});
      setSerialSearchTexts({});
      setExpandedWarehouses([]);
      setExpandedItems([]);
      setPackageSourceSelection("custom");
    }, [selectedItems, pack, packageTypeList, onOk]);

    const handleWarehouseCancel = () => {
      setSelectedItems({});
      setWarehouseSearchText("");
      setItemSearchTexts({});
      setSerialSearchTexts({});
      setExpandedWarehouses([]);
      setExpandedItems([]);
      // Don't set packageSourceSelection here - let the useEffect handle it based on props
    };

    const handleRemoveItem = (itemId) => {
      setSelectedItems((prev) => {
        const newSelected = { ...prev };
        delete newSelected[itemId];
        return newSelected;
      });
    };

    const selectedCount = Object.keys(selectedItems).length;
    const totalSerials = Object.values(selectedItems).reduce(
      (sum, item) => sum + item.serials.length,
      0
    );

    const onFinish = (values) => {
      const hasWarehouseSelection = Object.keys(selectedItems).length > 0;
      if (hasWarehouseSelection && !pack?.inventoryItemId) {
        message.error(
          "Cannot mix warehouse items with custom package. Please clear warehouse selections or switch to From Warehouse tab."
        );
        return;
      }

      const fields = {
        id: pack?.id || "",
        name: packageType?.name || "",
        action: null,
        quantity: values?.quantity || 0,
        barcode: values?.barcode
      };
      fields.packageTypeId = packageType?.id || "";
      if (values?.name?.toLowerCase() === "custom") {
        fields.maxLength = values.maxLength || 0;
        fields.maxWeight = values.maxWeight || 0;
        fields.maxVolume = values.maxVolume || 0;
        fields.maxHeight = values.maxHeight || 0;
      } else {
        fields.maxLength = packageType?.maxLength || 0;
        fields.maxWeight = packageType?.maxWeight || 0;
        fields.maxVolume = packageType?.maxVolume || 0;
      }

      onOk && onOk(fields);
    };

    const hasWarehouseItems = Object.keys(selectedItems).length > 0;
    const hasCustomFormData =
      formPack.getFieldValue("quantity") || formPack.getFieldValue("packageTypeId");
    const isEditingWarehousePackage = pack?.inventoryItemId;
    const isEditingCustomPackage = pack && !pack.inventoryItemId;
    const isCustomDisabled = hasWarehouseItems || isEditingWarehousePackage;
    const isWarehouseDisabled =
      isEditingCustomPackage || (hasCustomFormData && !pack?.inventoryItemId);

    return (
      <div className="PackForm">
        {!warehouseOnly && !customOnly && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #d9d9d9" }}>
              <div
                onClick={() => handlePackageSourceTabClick("custom")}
                style={{
                  padding: "12px 24px",
                  cursor: isCustomDisabled ? "not-allowed" : "pointer",
                  borderBottom:
                    packageSourceSelection === "custom"
                      ? "2px solid #1890ff"
                      : "2px solid transparent",
                  color: isCustomDisabled
                    ? "#d9d9d9"
                    : packageSourceSelection === "custom"
                    ? "#1890ff"
                    : "#666",
                  fontWeight: packageSourceSelection === "custom" ? 600 : 400,
                  transition: "all 0.3s",
                  opacity: isCustomDisabled ? 0.5 : 1
                }}
              >
                Custom
              </div>
              <div
                onClick={() => handlePackageSourceTabClick("warehouse")}
                style={{
                  padding: "12px 24px",
                  cursor: isWarehouseDisabled ? "not-allowed" : "pointer",
                  borderBottom:
                    packageSourceSelection === "warehouse"
                      ? "2px solid #1890ff"
                      : "2px solid transparent",
                  color: isWarehouseDisabled
                    ? "#d9d9d9"
                    : packageSourceSelection === "warehouse"
                    ? "#1890ff"
                    : "#666",
                  fontWeight: packageSourceSelection === "warehouse" ? 600 : 400,
                  transition: "all 0.3s",
                  opacity: isWarehouseDisabled ? 0.5 : 1
                }}
              >
                From Warehouse
              </div>
            </div>
          </div>
        )}
        {packageSourceSelection === "warehouse" && (
          <div style={{ marginBottom: 24 }}>
            {!pickupLocationId ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Text disabled>
                  Select a pickup location to view available warehouses.
                </Text>
              </div>
            ) : warehouses.length === 0 && !loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Text type="warning">No warehouses found for the selected location.</Text>
              </div>
            ) : (
              <>
                <Table
                  dataSource={sortedWarehouseIds
                    .map((warehouseId) => {
                      const warehouse = warehouses.find((w) => w.id === warehouseId);
                      if (!warehouse) return null;
                      return {
                        key: warehouse.id,
                        id: warehouse.id,
                        name: warehouse.name,
                        itemCount: filteredInventory[warehouse.id]?.length || 0
                      };
                    })
                    .filter(Boolean)}
                  columns={[
                    {
                      title: "Warehouse",
                      dataIndex: "name",
                      key: "name",
                      sorter: (warehouseA, warehouseB) =>
                        warehouseA.name.localeCompare(warehouseB.name)
                    },
                    {
                      title: "Items",
                      dataIndex: "itemCount",
                      key: "itemCount",
                      width: 100,
                      sorter: (warehouseA, warehouseB) =>
                        warehouseA.itemCount - warehouseB.itemCount
                    }
                  ]}
                  pagination={false}
                  rowKey="id"
                  scroll={{ x: "max-content" }}
                  tableLayout="auto"
                  expandable={{
                    expandedRowKeys: expandedWarehouses,
                    onExpandedRowsChange: setExpandedWarehouses,
                    expandedRowRender: (warehouse) => {
                      const warehouseItems = filteredInventory[warehouse.id] || [];
                      const itemSearchText = itemSearchTexts[warehouse.id] || "";
                      const filteredWarehouseItems = itemSearchText
                        ? warehouseItems.filter((item) => {
                            const searchLower = itemSearchText.toLowerCase();
                            return (
                              item.itemId?.toLowerCase().includes(searchLower) ||
                              item.name?.toLowerCase().includes(searchLower) ||
                              item.description?.toLowerCase().includes(searchLower)
                            );
                          })
                        : warehouseItems;

                      const inventoryColumns = [
                        {
                          title: "Item ID",
                          dataIndex: "itemId",
                          key: "itemId",
                          width: 100,
                          render: (text) => (
                            <Tooltip title={text}>
                              <span>{text}</span>
                            </Tooltip>
                          )
                        },
                        {
                          title: "Name",
                          dataIndex: "name",
                          key: "name",
                          width: 100,
                          render: (text) => (
                            <Tooltip title={text}>
                              <span
                                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                              >
                                {text}
                              </span>
                            </Tooltip>
                          )
                        },
                        {
                          title: "Quantity",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: 100,
                          render: (text) => (
                            <Tooltip title={text}>
                              <span>{text}</span>
                            </Tooltip>
                          )
                        },
                        {
                          title: "Package Type",
                          key: "packageType",
                          width: 100,
                          render: (_, item) => {
                            const itemPackageType = packageTypeList?.find(
                              (pt) => pt.id === item.packageTypeId
                            );
                            return itemPackageType ? (
                              <Tooltip title={itemPackageType.name}>
                                <span>{itemPackageType.name}</span>
                              </Tooltip>
                            ) : (
                              <Text type="secondary">-</Text>
                            );
                          }
                        },
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: 100,
                          render: (text) =>
                            text ? (
                              <Tooltip title={text}>
                                <span
                                  style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                                >
                                  {text}
                                </span>
                              </Tooltip>
                            ) : (
                              <Text type="secondary">-</Text>
                            )
                        }
                      ];
                      return (
                        <div
                          style={{
                            padding: "16px",
                            backgroundColor: "#fafafa",
                            borderRadius: "4px"
                          }}
                        >
                          <Input
                            placeholder="Search item"
                            prefix={<SearchOutlined />}
                            value={itemSearchText}
                            onChange={(e) =>
                              setItemSearchTexts({
                                ...itemSearchTexts,
                                [warehouse.id]: e.target.value
                              })
                            }
                            allowClear
                            style={{ width: "100%", marginBottom: 12 }}
                          />
                          <Table
                            dataSource={filteredWarehouseItems}
                            columns={inventoryColumns}
                            pagination={false}
                            rowKey="id"
                            loading={loading}
                            size="small"
                            tableLayout="auto"
                            scroll={{ x: "max-content" }}
                            expandable={{
                              expandedRowKeys: expandedItems,
                              onExpandedRowsChange: setExpandedItems,
                              expandedRowRender: (item) => {
                                const itemSerials = item.serialNumbersList || [];
                                const serialSearchText = serialSearchTexts[item.id] || "";
                                const filteredSerials = serialSearchText
                                  ? itemSerials.filter((serial) => {
                                      const searchLower = serialSearchText.toLowerCase();
                                      return (
                                        serial.serialNumber
                                          ?.toLowerCase()
                                          .includes(searchLower) ||
                                        serial.itemAttribute
                                          ?.toLowerCase()
                                          .includes(searchLower)
                                      );
                                    })
                                  : itemSerials;
                                const allSerialSelected =
                                  filteredSerials.length > 0 &&
                                  filteredSerials.every((serial) =>
                                    isSerialSelected(item.id, serial)
                                  );
                                const someSerialSelected = filteredSerials.some(
                                  (serial) => isSerialSelected(item.id, serial)
                                );
                                const getPickupLabel = (pickupId) => {
                                  const pickup = pickups.find((p) => p.id === pickupId);
                                  return pickup
                                    ? `${pickup.title} ${pickup.position}`
                                    : `Pickup ${pickupId.slice(-4)}`;
                                };
                                return (
                                  <div
                                    style={{
                                      padding: "12px",
                                      backgroundColor: "#f0f0f0",
                                      borderRadius: "4px",
                                      marginTop: "8px"
                                    }}
                                  >
                                    <Input
                                      placeholder="Search by serial or attribute"
                                      prefix={<SearchOutlined />}
                                      value={serialSearchText}
                                      onChange={(e) =>
                                        setSerialSearchTexts({
                                          ...serialSearchTexts,
                                          [item.id]: e.target.value
                                        })
                                      }
                                      allowClear
                                      style={{ width: "100%", marginBottom: 12 }}
                                    />
                                    <Table
                                      key={`serials-${item.id}-${
                                        selectedItems[item.id]?.serials?.length || 0
                                      }`}
                                      dataSource={filteredSerials}
                                      columns={[
                                        {
                                          title: "Select",
                                          key: "select",
                                          width: 100,
                                          headerCellRenderer: () => (
                                            <Checkbox
                                              checked={allSerialSelected}
                                              indeterminate={
                                                someSerialSelected && !allSerialSelected
                                              }
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  filteredSerials.forEach((serial) => {
                                                    const isAllocated =
                                                      !!allocatedSerials[
                                                        serial.serialNumber
                                                      ];
                                                    if (
                                                      !isSerialSelected(
                                                        item.id,
                                                        serial
                                                      ) &&
                                                      !isAllocated
                                                    ) {
                                                      handleSerialSelection(
                                                        item.id,
                                                        serial,
                                                        true
                                                      );
                                                    }
                                                  });
                                                } else {
                                                  filteredSerials.forEach((serial) => {
                                                    if (
                                                      isSerialSelected(item.id, serial)
                                                    ) {
                                                      handleSerialSelection(
                                                        item.id,
                                                        serial,
                                                        false
                                                      );
                                                    }
                                                  });
                                                }
                                              }}
                                            />
                                          ),
                                          render: (_, serial) => {
                                            const isAllocated =
                                              !!allocatedSerials[serial.serialNumber];
                                            const isChecked = isSerialSelected(
                                              item.id,
                                              serial
                                            );
                                            return (
                                              <Checkbox
                                                checked={isChecked}
                                                disabled={isAllocated}
                                                onChange={(e) =>
                                                  handleSerialSelection(
                                                    item.id,
                                                    serial,
                                                    e.target.checked
                                                  )
                                                }
                                              />
                                            );
                                          }
                                        },
                                        {
                                          title: "Serial Number",
                                          dataIndex: "serialNumber",
                                          key: "serialNumber",
                                          width: 100,
                                          render: (text) => (
                                            <Tooltip title={text}>
                                              <Tag
                                                color="blue"
                                                style={{
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis"
                                                }}
                                              >
                                                {text}
                                              </Tag>
                                            </Tooltip>
                                          )
                                        },
                                        {
                                          title: "Attribute",
                                          dataIndex: "itemAttribute",
                                          key: "itemAttribute",
                                          width: 100,
                                          render: (text) =>
                                            text ? (
                                              <Tooltip title={text}>
                                                <Tag color="green">{text}</Tag>
                                              </Tooltip>
                                            ) : (
                                              <Text type="secondary">-</Text>
                                            )
                                        },
                                        {
                                          title: "PO",
                                          dataIndex: "po",
                                          key: "po",
                                          width: 100,
                                          render: (text) =>
                                            text ? (
                                              <Tooltip title={text}>
                                                <span>{text}</span>
                                              </Tooltip>
                                            ) : (
                                              <Text type="secondary">-</Text>
                                            )
                                        },
                                        {
                                          title: "Pickup",
                                          key: "pickupAllocation",
                                          width: 80,
                                          render: (_, serial) => {
                                            const allocatedPickupId =
                                              allocatedSerials[serial.serialNumber];
                                            if (allocatedPickupId) {
                                              const pickupLabel =
                                                getPickupLabel(allocatedPickupId);
                                              return (
                                                <Tooltip
                                                  title={`Already selected in ${pickupLabel}`}
                                                >
                                                  <Tag
                                                    color="orange"
                                                    style={{
                                                      fontSize: "10px",
                                                      padding: "0 4px"
                                                    }}
                                                  >
                                                    {pickupLabel}
                                                  </Tag>
                                                </Tooltip>
                                              );
                                            }
                                            return <Text type="secondary">-</Text>;
                                          }
                                        }
                                      ]}
                                      pagination={false}
                                      rowKey="serialNumber"
                                      size="small"
                                    />
                                  </div>
                                );
                              }
                            }}
                          />
                        </div>
                      );
                    }
                  }}
                  loading={loading}
                  size="middle"
                />
                {selectedCount > 0 && (
                  <>
                    <Divider />
                    <div>
                      <Text strong>Selection Summary:</Text>
                      <div style={{ marginTop: 12 }}>
                        {Object.entries(selectedItems).map(([itemId, selection]) => {
                          const itemData =
                            selection.item || inventoryItems.find((i) => i.id === itemId);
                          if (!itemData) return null;
                          const itemPackageType = packageTypeList?.find(
                            (pt) => pt.id === itemData.packageTypeId
                          );
                          return (
                            <div
                              key={itemId}
                              style={{
                                marginBottom: 12,
                                padding: 8,
                                background: "#f5f5f5",
                                position: "relative"
                              }}
                            >
                              <CloseCircleOutlined
                                onClick={() => handleRemoveItem(itemId)}
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  fontSize: 18,
                                  color: "#ff4d4f",
                                  cursor: "pointer"
                                }}
                              />
                              <Text strong>
                                Item {itemData.itemId} - {itemData.name}
                              </Text>
                              <div style={{ marginTop: 4 }}>
                                {itemPackageType && (
                                  <Text
                                    type="secondary"
                                    style={{ display: "block", marginBottom: 4 }}
                                  >
                                    Package: <strong>{itemPackageType.name}</strong>
                                    {itemPackageType.maxWeight && (
                                      <>
                                        {" | Weight: "}
                                        <strong>
                                          {itemPackageType.maxWeight}
                                          {window.REACT_APP_DD_ENV === "ccpexpress"
                                            ? "kg"
                                            : "Lbs"}
                                        </strong>
                                      </>
                                    )}
                                    {itemPackageType.maxLength && (
                                      <>
                                        {" | Size: "}
                                        <strong>
                                          {itemPackageType.maxLength}
                                          {window.REACT_APP_DD_ENV === "ccpexpress"
                                            ? "cm"
                                            : "in"}
                                        </strong>
                                      </>
                                    )}
                                  </Text>
                                )}
                                {selection.serials.length > 0 && (
                                  <div>
                                    {selection.serials.map((serial, idx) => (
                                      <div key={idx} style={{ marginBottom: 4 }}>
                                        <Tag color="blue">{serial.serialNumber}</Tag>
                                        {serial.itemAttribute && (
                                          <Tag color="green" style={{ marginLeft: 4 }}>
                                            {serial.itemAttribute}
                                          </Tag>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {packageSourceSelection === "custom" && (
          <Form
            scrollToFirstError={true}
            layout="vertical"
            form={formPack}
            hideRequiredMark
            colon={false}
            onFinish={onFinish}
            initialValues={pack}
          >
            <Form.Item
              name="packageTypeId"
              rules={[{ required: true, message: "Required" }]}
              className="type"
            >
              <Radio.Group onChange={onChangeType} value={packageType?.id}>
                {sortBy(packageTypeList, "maxWeight").map(
                  (item) =>
                    item.name !== "Custom" && (
                      <Radio
                        key={item?.id}
                        value={item?.id}
                        checked={item?.id === pack?.id}
                      >
                        {item?.name}
                      </Radio>
                    )
                )}
              </Radio.Group>
            </Form.Item>
            <div className="normal-type">
              <div className="box">
                <img src="/box.png" alt="" />
              </div>
              {packageType !== "custom" && (
                <div className="types">
                  <p>
                    Max Weight:{" "}
                    <b>
                      {packageType?.maxWeight || "-"}{" "}
                      {window.REACT_APP_DD_ENV === "ccpexpress" ? "kg" : "Lbs"}
                    </b>
                  </p>
                  <p>
                    Max Length:{" "}
                    <b>
                      {packageType?.maxLength || "-"}{" "}
                      {window.REACT_APP_DD_ENV === "ccpexpress"
                        ? "centimeters"
                        : "Inches"}
                    </b>
                  </p>
                  <p>
                    Max Volume:{" "}
                    <b>
                      {packageType?.maxVolume || "-"}{" "}
                      {window.REACT_APP_DD_ENV === "ccpexpress" ? <>&#13221;</> : "Cuft"}
                    </b>
                  </p>
                </div>
              )}
            </div>

            <div className="pack-info">
              <Row>
                <Col span="8">
                  <Form.Item
                    label="Quantity"
                    name="quantity"
                    rules={[
                      { required: true, message: "Required" },
                      () => ({
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject();
                          }
                          if (Number(value) < 1) {
                            return Promise.reject("Invalid data");
                          }
                          if (Number(value) < quantityCurrent) {
                            return Promise.reject("Invalid data");
                          }
                          return Promise.resolve();
                        }
                      })
                    ]}
                  >
                    <Input type="number" placeholder="Quantity" />
                  </Form.Item>
                </Col>
                <Col span="16">
                  <Form.Item
                    label="Barcode number (optional)"
                    name="barcode"
                    rules={[{ required: false }]}
                  >
                    <Input placeholder="Barcode number" />
                  </Form.Item>
                </Col>
              </Row>
              {/* // only show pool packages if they exist */}
              {Array.isArray(poolPackages) && poolPackages.length > 0 && (
                <Row>
                  <Col span="24">
                    <h3>Pool Package IDs</h3>
                    <List
                      dataSource={poolPackages}
                      renderItem={(listItem) => {
                        return (
                          <List.Item>
                            <h4>{listItem.packageTypeName}</h4>
                            {listItem.id}
                          </List.Item>
                        );
                      }}
                    ></List>
                  </Col>
                </Row>
              )}
            </div>
          </Form>
        )}
      </div>
    );
  }
);

PackForm.displayName = "PackForm";

export default PackForm;
